/**
 * CustomArtCard — card memoizado da galeria /custom-arts.
 *
 * Exibe:
 * - Thumb (aspect-video) com fallback para ícone quando URL não é HTTP.
 * - Nome + badge de origem color-coded (TH-55166):
 *     • "Priceshow" → primary gold
 *     • "Minha"     → success green
 *     • <fornecedor> → info blue (com título para leitores de tela)
 * - Badge de validade herdado da lógica já existente em CustomArtsPage.
 * - Botões contextuais: "Editar" (scope=tenant + permissão), "Detalhes".
 *
 * A lógica de classificação de validade permanece em CustomArtsPage
 * (mesma função `classifyValidity`) — o card recebe a string e o tone
 * já prontos para não duplicar código.
 */

import { memo, useRef, useEffect } from "react";
import { ImageIcon } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { StatusPill, type StatusTone } from "@/components/ui/StatusPill";
import { cn } from "@/lib/utils";
import type { CustomArtRead } from "@/hooks/useCustomArts";
import { useArtViewTracker } from "@/hooks/useArtViewTracker";

// ---------------------------------------------------------------------------
// Badge de origem (TH-55166)
// ---------------------------------------------------------------------------
// "Priceshow" → warn (gold, mapeia pra --color-warning)
// "Minha"     → success (verde)
// <fornecedor>→ azul inline (Badge não tem variante info; usamos classe direta)

function OriginBadge({ label }: { label: string }) {
  if (label === "Priceshow") {
    return <Badge variant="warn">{label}</Badge>;
  }
  if (label === "Minha") {
    return <Badge variant="success">{label}</Badge>;
  }
  // Fornecedor patrocinado — badge azul com title para truncado.
  return (
    <span
      title={label}
      className="inline-flex max-w-[120px] items-center truncate rounded-full bg-[var(--color-info)]/14 px-2 py-0.5 text-[11px] font-medium text-[var(--color-info)]"
      aria-label={`Fornecedor: ${label}`}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Props do card
// ---------------------------------------------------------------------------

export interface CustomArtCardProps {
  art: CustomArtRead;
  /** Descrição legível do escopo de loja/área, ex: "Loja A • Bebidas". */
  scopeDescription: string;
  /** Label de validade derivado de classifyValidity. */
  validityLabel: string;
  /** Tom visual da validade. */
  validityTone: StatusTone;
  /** Se true, exibe botão "Editar" (origin=Minha + permissão). */
  canEdit: boolean;
  /** Clique em qualquer parte do card exceto nos botões. */
  onDetails: () => void;
  onEdit: () => void;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export const CustomArtCard = memo(function CustomArtCard({
  art,
  scopeDescription,
  validityLabel,
  validityTone,
  canEdit,
  onDetails,
  onEdit,
}: CustomArtCardProps) {
  const isWebUrl = /^https?:\/\//i.test(art.art_url);
  const cardRef = useRef<HTMLElement>(null);
  const { observerRef } = useArtViewTracker(art.id);

  // Registrar visualização quando card entra no viewport
  useEffect(() => {
    if (!observerRef.current || !cardRef.current) return;
    observerRef.current.observe(cardRef.current);
    return () => {
      if (cardRef.current) {
        observerRef.current?.unobserve(cardRef.current);
      }
    };
  }, [observerRef]);

  return (
    <article
      ref={cardRef}
      className={cn(
        "flex flex-col overflow-hidden rounded-lg border border-[var(--color-border)]",
        "bg-[var(--color-card)] transition-shadow hover:shadow-md",
        "focus-within:ring-2 focus-within:ring-[var(--color-ring)]",
      )}
      aria-label={`Arte: ${art.name}`}
    >
      {/* Thumb */}
      <button
        type="button"
        onClick={onDetails}
        className="flex aspect-video items-center justify-center overflow-hidden bg-[var(--color-background)] focus-visible:outline-none"
        aria-label={`Ver detalhes de ${art.name}`}
        tabIndex={0}
      >
        {isWebUrl ? (
          <img
            src={art.art_url}
            alt={art.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-[var(--color-muted-foreground)]">
            <ImageIcon className="h-8 w-8" aria-hidden="true" />
            <span className="font-mono text-[10px] tabular-nums">
              {art.width}×{art.height}
            </span>
          </div>
        )}
      </button>

      {/* Corpo */}
      <div className="flex flex-1 flex-col gap-2 p-3">
        {/* Nome + badge de origem */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-sm font-medium text-[var(--color-foreground)]">
            {art.name}
          </h3>
          <OriginBadge label={art.origin_label} />
        </div>

        {/* Escopo loja/área */}
        {scopeDescription ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {scopeDescription}
          </p>
        ) : null}

        {/* Rodapé: status ativa/inativa + validade + tamanho */}
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-1">
            {!art.active && (
              <StatusPill tone="muted">Inativa</StatusPill>
            )}
            <StatusPill tone={validityTone}>{validityLabel}</StatusPill>
          </div>
          <span className="font-mono text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
            {(art.file_size_bytes / 1024).toFixed(0)} KB
          </span>
        </div>

        {/* Botões de ação */}
        <div className="flex gap-2 pt-1">
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              aria-label={`Editar ${art.name}`}
            >
              Editar
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDetails();
            }}
            aria-label={`Detalhes de ${art.name}`}
          >
            Detalhes
          </Button>
        </div>
      </div>
    </article>
  );
});
